import React, { useState, useEffect, useRef } from 'react'; // תקן את הפסיק החסר
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  DollarSign,
  PieChart,
  Info,
  Heart,
  AlertCircle,Share2,Copy,Search,Check ,
   TrendingUp, AlertTriangle, BarChart3, Activity , HelpCircle,
} from 'lucide-react';


import api from '../services/api';
import { ProductComparisonTable } from '../components/CompactComparisonTable';
import ReturnsChart from '../components/ReturnsChart';
import { getSpecializationConfigs, getEnhancedSpecializationMatch } from '../config/fundConfigurations';
import FundComparisonComponent from '../components/FundComparisonComponent';
import { getDisplayName } from '../config/fundNamesConfig';
import ReturnsSection from '../components/ReturnsSection';
import TooltipPortal from '../components/TooltipPortal'; // וודא שהנתיב נכון
import OptimizedSearchAutocomplete from '../components/OptimizedSearchAutocomplete';
import FavoritesButton from '../components/FavoritesButton'; 
import FundDataExportSection from '../components/FundDataExportSection';
import ContactButton from '../components/ContactButton';
import CompactExportButton from '../components/CompactExportButton';
import LeadModal from '../components/FloatingElements';
import { saveContact } from '../services/firebase/contactService';
import { toast } from 'react-hot-toast'; 
import RouteScannerCTA from '../components/RouteScannerCTA';


const ReturnsDisplay = ({ returns, lastUpdate, productName, productId, categoryId }) => {
    const [copied, setCopied] = useState(false);
    const [shared, setShared] = useState(false);

    const monthNames = {
        '01': 'ינואר', '02': 'פברואר', '03': 'מרץ', '04': 'אפריל',
        '05': 'מאי', '06': 'יוני', '07': 'יולי', '08': 'אוגוסט',
        '09': 'ספטמבר', '10': 'אוקטובר', '11': 'נובמבר', '12': 'דצמבר',
    };

    const getFormattedDate = (dateString) => {
        if (!dateString || dateString.length !== 6) return null;
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const monthName = monthNames[month] || '';
        return `${monthName} ${year}`;
    };

    const formattedLastUpdate = getFormattedDate(lastUpdate);

    const cumulativeReturns = [
        { label: `תשואה מצטברת לחודש ${formattedLastUpdate}`, value: returns.month1 },
        { label: 'תשואה מצטברת מתחילת שנה', value: returns.ytd },
        { label: 'תשואה מצטברת ל-12 החודשים האחרונים', value: returns.year1 },
        { label: 'תשואה מצטברת ל-24 החודשים האחרונים', value: returns.year2 },
        { label: 'תשואה מצטברת ל-36 החודשים האחרונים', value: returns.year3 },
        { label: 'תשואה מצטברת ל-48 החודשים האחרונים', value: returns.year4 },
        { label: 'תשואה מצטברת ל-60 החודשים האחרונים', value: returns.year5 },
    ].filter(item => item.value !== undefined && item.value !== null);

    const averageReturns = [
        { label: 'תשואה שנתית ממוצעת לשנה ב-2 שנים האחרונות', value: returns.avgAnnual2Years },
        { label: 'תשואה שנתית ממוצעת לשנה ב-3 שנים האחרונות', value: returns.avgAnnual3Years },
        { label: 'תשואה שנתית ממוצעת לשנה ב-4 שנים האחרונות', value: returns.avgAnnual4Years },
        { label: 'תשואה שנתית ממוצעת לשנה ב-5 שנים האחרונות', value: returns.avgAnnual5Years },
    ].filter(item => item.value !== undefined && item.value !== null);

    // יצירת טקסט מסודר לשיתוף/העתקה
    const createFormattedText = () => {
        const productUrl = `https://www.gemelnet.ai/product/${categoryId}/${productId}`;
        
        const cumulativeText = cumulativeReturns
            .map(item => `📊 ${item.label}: **${item.value.toFixed(2)}%**`)
            .join('\n');
            
        const averageText = averageReturns
            .map(item => `📈 ${item.label}: **${item.value.toFixed(2)}%**`)
            .join('\n');

        return `🎯 **סיכום תשואות - ${productName}**
\n\n 
📊 **תשואות מצטברות:**
\n\n 
${cumulativeText}
\n────────────────────────────\n\n 
\n
📈 **תשואות שנתיות ממוצעות:**
\n\n 
\n
${averageText}
\n
\n────────────────────────────\n\n 
\n
🗓️ **נכונות הנתונים:** ${formattedLastUpdate || 'לא זמין'}
\n
🔗 **לפרטים נוספים:**
\n
${productUrl}

💡 **מקור:** מערכת גמל נט AI - השוואת מוצרי חיסכון ופנסיה`;
    };

    const handleCopy = async () => {
        try {
            const textToCopy = createFormattedText();
            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            toast.success("התשואות הועתקו בהצלחה! 📋");
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            toast.error("שגיאה בהעתקה");
        }
    };

    const handleShare = async () => {
        const textToShare = createFormattedText();
        const productUrl = `https://www.gemelnet.ai/product/${categoryId}/${productId}`;
        
        if (navigator.share) {
            // Web Share API זמין
            try {
                await navigator.share({
                    title: `תשואות ${productName}`,
                    text: textToShare,
                    url: productUrl
                });
                setShared(true);
                toast.success("שותף בהצלחה! 🚀");
                setTimeout(() => setShared(false), 2000);
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Share failed:', err);
                    // fallback להעתקה
                    await handleCopy();
                }
            }
        } else {
            // fallback לקישורי שיתוף רגילים
            const encodedText = encodeURIComponent(textToShare);
            const encodedUrl = encodeURIComponent(productUrl);
            
            // יצירת תפריט שיתוף
            const shareOptions = [
                {
                    name: 'WhatsApp',
                    url: `https://wa.me/?text=${encodedText}`,
                    color: 'bg-green-500 hover:bg-green-600'
                },
                {
                    name: 'Telegram',
                    url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
                    color: 'bg-blue-500 hover:bg-blue-600'
                },
                {
                    name: 'Twitter',
                    url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
                    color: 'bg-black hover:bg-gray-800'
                },
                {
                    name: 'Facebook',
                    url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
                    color: 'bg-blue-600 hover:bg-blue-700'
                }
            ];
            
            // הצגת modal עם אפשרויות שיתוף
            const modal = document.createElement('div');
            modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
            modal.innerHTML = `
                <div class="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
                    <h3 class="text-lg font-bold text-gray-900 mb-4 text-center">שתף תשואות</h3>
                    <div class="space-y-3">
                        ${shareOptions.map(option => `
                            <button onclick="window.open('${option.url}', '_blank'); document.body.removeChild(this.closest('.fixed'))" 
                                    class="w-full ${option.color} text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2">
                                ${option.name}
                            </button>
                        `).join('')}
                        <button onclick="navigator.clipboard.writeText('${textToShare.replace(/'/g, "\\'")}').then(() => { document.body.removeChild(this.closest('.fixed')); alert('הועתק ללוח!'); })" 
                                class="w-full bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 rounded-lg font-medium transition-colors">
                            📋 העתק ללוח
                        </button>
                        <button onclick="document.body.removeChild(this.closest('.fixed'))" 
                                class="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-medium transition-colors">
                            ביטול
                        </button>
                    </div>
                </div>
            `;
            
            document.body.appendChild(modal);
            
            // סגירה בלחיצה על הרקע
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    document.body.removeChild(modal);
                }
            });
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 md:p-6 mb-6 md:mb-8">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 md:h-6 md:w-6 text-emerald-600" />
                    סיכום תשואות
                </div>

                <div className="flex items-center gap-2">
                    {/* כפתור שיתוף */}
                    <button
                        onClick={handleShare}
                        className="p-2 rounded-full text-gray-500 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                        title="שתף תשואות"
                    >
                        {shared ? (
                            <Check size={16} className="text-green-500" />
                        ) : (
                            <Share2 size={16} />
                        )}
                    </button>

                    {/* כפתור העתקה */}
                    <button
                        onClick={handleCopy}
                        className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
                        title="העתק תשואות"
                    >
                        {copied ? (
                            <Check size={16} className="text-green-500" />
                        ) : (
                            <Copy size={16} />
                        )}
                    </button>
                </div>
            </h3>
            
            <div className="flex flex-col gap-2 relative">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* עמודה ימנית: תשואות מצטברות */}
                    <div className="flex flex-col gap-1 text-xs md:text-sm order-1 md:order-1">
                        <h5 className="font-semibold text-gray-700">תשואות מצטברות:</h5>
                        {cumulativeReturns.map((item, index) => (
                            <div key={index} className="flex justify-between items-center gap-1">
                                <span className="text-gray-600 font-medium">{item.label}:</span>
                                <span className="font-bold bg-yellow-200 text-gray-900">{item.value.toFixed(2)}%</span>
                            </div>
                        ))}
                    </div>

                    {/* עמודה שמאלית: תשואות שנתיות ממוצעות */}
                    <div className="flex flex-col gap-1 text-xs md:text-sm order-2 md:order-2">
                        <h5 className="font-semibold text-gray-700">תשואות שנתיות ממוצעות:</h5>
                        {averageReturns.map((item, index) => (
                            <div key={index} className="flex justify-between items-center gap-1">
                                <span className="text-gray-600 font-medium">{item.label}:</span>
                                <span className="font-bold bg-yellow-200 text-gray-900">{item.value.toFixed(2)}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
const ExposuresSection = ({ product }) => {
  const tooltipRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Refs ו-states עבור tooltips של מדדי הסיכון
  const sharpeTooltipRef = useRef(null);
  const alphaTooltipRef = useRef(null);
  const stdDevTooltipRef = useRef(null);
  const liquidityTooltipRef = useRef(null);
  
  const [showSharpeTooltip, setShowSharpeTooltip] = useState(false);
  const [showAlphaTooltip, setShowAlphaTooltip] = useState(false);
  const [showStdDevTooltip, setShowStdDevTooltip] = useState(false);
  const [showLiquidityTooltip, setShowLiquidityTooltip] = useState(false);

  // זיהוי מובייל
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 🔧 שימוש בנתונים מוכנים מהמוצר
  const stocksExposure = product.exposures?.stocks || 0;
  const foreignExposure = product.exposures?.foreign || 0;
  const currencyExposure = product.exposures?.currency || 0; 
  const domesticExposure = foreignExposure >= 100 ? 0 : Math.max(0, 100 - foreignExposure);
  const illiquidAssets = product.performance?.liquidAssetsPercent ? 
    (100 - product.performance.liquidAssetsPercent) : 0;

  // מדדי סיכון
  const alpha = product.performance?.alpha || 0;
  const sharpeRatio = product.performance?.sharpeRatio || 0;
  const standardDeviation = product.performance?.standardDeviation || 0;
  const liquidityRatio = product.performance?.liquidAssetsPercent || 0;

  // פונקציות עזר למדדי סיכון - נשארות כמו שהן
  const getAlphaPercentage = (alpha) => {
    const normalizedAlpha = Math.max(-10, Math.min(10, alpha));
    return ((normalizedAlpha + 10) / 20) * 100;
  };

  const getSharpePercentage = (sharpe) => {
    const normalizedSharpe = Math.max(0, Math.min(3, sharpe));
    return (normalizedSharpe / 3) * 100;
  };

  const getStandardDeviationPercentage = (stdDev) => {
    const normalizedStdDev = Math.max(0, Math.min(25, stdDev));
    return ((25 - normalizedStdDev) / 25) * 100;
  };

  const getAlphaColor = (alpha) => {
    if (alpha > 1) return "#10B981"; // ירוק - טוב
    if (alpha > 0) return "#F59E0B"; // צהוב - בינוני
    return "#EF4444"; // אדום - לא טוב
  };

  const getSharpeColor = (sharpe) => {
    if (sharpe > 1.5) return "#10B981"; // ירוק - מעולה
    if (sharpe > 1) return "#3B82F6"; // כחול - טוב
    if (sharpe > 0.5) return "#F59E0B"; // צהוב - בינוני
    return "#EF4444"; // אדום - חלש
  };

  const getStdDevColor = (stdDev) => {
    if (stdDev < 5) return "#10B981"; // ירוק - סיכון נמוך
    if (stdDev < 10) return "#3B82F6"; // כחול - סיכון בינוני-נמוך
    if (stdDev < 15) return "#F59E0B"; // צהוב - סיכון בינוני
    return "#EF4444"; // אדום - סיכון גבוה
  };

  // פונקציה לסגירת כל הטוטיפים
  const closeAllTooltips = () => {
    setShowTooltip(false);
    setShowSharpeTooltip(false);
    setShowAlphaTooltip(false);
    setShowStdDevTooltip(false);
    setShowLiquidityTooltip(false);
  };

   return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 p-4 md:p-6 mb-6 md:mb-8">
      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center gap-2">
        <PieChart className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
        פיזור חשיפה אפקטיבית במונחי דלתא
        <div
          ref={tooltipRef}
          className="relative inline-flex items-center"
          onMouseEnter={() => !isMobile && setShowTooltip(true)}
          onMouseLeave={() => !isMobile && setShowTooltip(false)}
          onClick={() => setShowTooltip(!showTooltip)}
    >
          <HelpCircle className="h-4 w-4 text-blue-500 hover:text-blue-600 cursor-help transition-colors" />

          {/* הטוולטיפ נשאר כמו שהוא */}
          {showTooltip && (
            <>
              {/* טולטיפ למובייל */}
              {isMobile ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeAllTooltips}>
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-200 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="font-bold text-blue-600">🔀 חשיפה אפקטיבית (במונחי דלתא)</div>
                      <button 
                        onClick={closeAllTooltips}
                        className="text-gray-500 hover:text-red-500 transition-colors text-xl font-bold leading-none"
                        aria-label="סגור"
                      >
                        ×
                      </button>
                    </div>
                    <div className="space-y-3 text-gray-700">
                      <p className="text-sm">
                        כמה הקופה באמת חשופה למניות, לחו"ל ולמטבעות זרים – לא לפי מה שכתוב במדיניות, 
                        אלא לפי איך היא באמת מתנהגת בפועל.
                      </p>
                      <p className="text-sm text-orange-600 font-medium">
                        זה המדד שמראה את הסיכון האמיתי של הקופה שלך.
                      </p>
                      
                      <div className="bg-blue-100 rounded-lg p-3 space-y-1 text-sm">
                        <p className="text-green-700 font-medium">דלתא 1 = חשיפה מלאה</p>
                        <p className="text-yellow-700 font-medium">דלתא 0.5 = חצי חשיפה</p>
                        <p className="text-red-700 font-medium">דלתא 0 = בלי סיכון שוק בכלל</p>
                      </div>
                      
                      <div className="border-t border-blue-200 pt-3">
                        <div className="font-bold text-orange-600 mb-2">🐂 הטיפ של השור מגמל AI:</div>
                        <p className="text-sm text-gray-700">
                          כתוב במדיניות הקופה: 60% מניות, 40% חו"ל, 30% מט"ח?
                          נשמע מרשים – אבל המספרים האלה לא מספרים את כל הסיפור.
                        </p>
                        <p className="text-sm text-blue-700 mt-1 font-medium">
                          רק הדלתא תגלה לך כמה הקופה באמת זזה עם השוק.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* טולטיפ לדקסטופ */
                <TooltipPortal targetRef={tooltipRef}>
                  <div className="bg-gray-900 text-white p-4 rounded-xl shadow-2xl max-w-xs md:max-w-md border border-gray-600 text-sm leading-relaxed">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-blue-300">🔀 חשיפה אפקטיבית (במונחי דלתא)</div>
                      <button 
                        onClick={closeAllTooltips}
                        className="text-white hover:text-red-300 transition-colors text-lg font-bold leading-none"
                        aria-label="סגור"
                      >
                        ×
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-gray-100">
                          כמה הקופה באמת חשופה למניות, לחו"ל ולמטבעות זרים – לא לפי מה שכתוב במדיניות, 
                          אלא לפי איך היא באמת מתנהגת בפועל.
                        </p>
                        <p className="text-yellow-200 mt-2">
                          זה המדד שמראה את הסיכון האמיתי של הקופה שלך.
                        </p>
                      </div>
                      
                      <div className="bg-gray-800 rounded-lg p-3 space-y-1">
                        <p className="text-green-300">דלתא 1 = חشיפה מלאה</p>
                        <p className="text-yellow-300">דלתא 0.5 = חצי חשיפה</p>
                        <p className="text-red-300">דלתא 0 = בלי סיכון שוק בכלל</p>
                      </div>
                      
                      <div className="border-t border-gray-600 pt-3">
                        <div className="font-bold text-orange-300 mb-2">🐂 הטיפ של השור מגמל AI:</div>
                        <p className="text-gray-100">
                          כתוב במדיניות הקופה: 60% מניות, 40% חו"ל, 30% מט"ח?
                          נשמע מרשים – אבל המספרים האלה לא מספרים את כל הסיפור.
                        </p>
                        <p className="text-blue-200 mt-1">
                          רק הדלתא תגלה לך כמה הקופה באמת זזה עם השוק.
                        </p>
                      </div>
                    </div>
                    
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                      <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </TooltipPortal>
              )}
            </>
          )}
        </div>
      </h3>
      
      <div className="md:grid md:grid-cols-2 md:gap-8">
        <div className="mb-6 md:mb-0">
          <h4 className="font-semibold text-gray-900 mb-3 md:mb-6 text-center text-sm md:text-base">
           פיזור מקומי מול גלובלי
          </h4>
          <div className="flex justify-center gap-4 md:gap-8">
            <CircularProgress
              percentage={domesticExposure}
              label="חשיפה לארץ"
              color="#F59E0B"
              size={80}
              strokeWidth={4}
              textSize="text-xs md:text-sm"
            />
            <CircularProgress
              percentage={foreignExposure}
              label="חשיפה לחו״ל"
              color="#3B82F6"
              size={80}
              strokeWidth={4}
              textSize="text-xs md:text-sm"
            />
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-3 md:mb-6 text-center text-sm md:text-base">
           מדדי הפיזור העיקריים והחשובים
          </h4>
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            <CircularProgress
              percentage={stocksExposure}
              label="חשיפה למניות"
              color="#10B981"
              size={60}
              strokeWidth={4}
              textSize="text-xs"
              labelSize="text-xs"
            />
            <CircularProgress
              percentage={currencyExposure}
              label="חשיפה למט״ח"
              color="#8B5CF6"
              size={60}
              strokeWidth={4}
              textSize="text-xs"
              labelSize="text-xs"
            />
            <CircularProgress
              percentage={illiquidAssets}
              label="נכסים לא סחירים"
              color="#EF4444"
              size={60}
              strokeWidth={4}
              textSize="text-xs"
              labelSize="text-xs"
            />
          </div>
        </div>
      </div>

      {/* מדדי סיכון - הקוד נשאר אותו דבר */}
      <div className="mt-8 md:mt-10">
        <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
          מדדי סיכון
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          
          {/* אלפא שנתית */}
          <div className="flex flex-col items-center relative">
            <div className="relative">
              <CircularProgress
                percentage={getAlphaPercentage(alpha)}
                label="אלפא שנתית"
                color={getAlphaColor(alpha)}
                size={70}
                strokeWidth={4}
                textSize="text-xs"
                labelSize="text-xs"
                showPercentage={false}
                value={alpha}
                unit=""
                maxValue={100}
              />
              <div
                ref={alphaTooltipRef}
                className="absolute -top-1 -right-1 cursor-help"
                onMouseEnter={() => !isMobile && setShowAlphaTooltip(true)}
                onMouseLeave={() => !isMobile && setShowAlphaTooltip(false)}
                onClick={() => setShowAlphaTooltip(!showAlphaTooltip)}
              >
                <HelpCircle className="h-3 w-3 text-blue-500 hover:text-blue-600 transition-colors" />
              </div>
            </div>

            {showAlphaTooltip && (
              <>
                {/* טולטיפ למובייל */}
                {isMobile ? (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeAllTooltips}>
                     <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200 max-w-sm w-full shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="font-bold text-green-600">🟢 אלפא שנתית</div>
                        <button 
                          onClick={closeAllTooltips}
                          className="text-gray-500 hover:text-red-500 transition-colors text-xl font-bold leading-none"
                          aria-label="סגור"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3 text-gray-700">
                        <p className="text-sm">
                          <strong>מה זה:</strong> תשואה עודפת (או חסרה) של הקופה ביחס למדד ייחוס, אחרי התאמה לרמת הסיכון.
                        </p>
                        <p className="text-sm text-orange-600 font-medium">
                          אלפא חיובית = הקופה "ניצחה את השוק".
                        </p>
                        
                        <div className="bg-green-100 rounded-lg p-3 space-y-1 text-sm">
                          <div className="font-bold mb-2">טווחים:</div>
                          <p className="text-green-700 font-medium">🟢 טוב מאוד: מעל 1.0%</p>
                          <p className="text-yellow-700 font-medium">🟡 בינוני: 0%–1.0%</p>
                          <p className="text-red-700 font-medium">🔴 שלילי: מתחת ל-0%</p>
                        </div>
                      </div>
                      <div className="flex justify-center mt-6">
            <button
            onClick={closeAllTooltips}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              הבנתי, תודה!
            </button>
          </div>
                    </div>
                  </div>
                ) : (
                  /* טולטיפ לדקסטופ */
                  <TooltipPortal targetRef={alphaTooltipRef}>
                    <div className="bg-gray-900 text-white p-4 rounded-xl shadow-2xl max-w-xs border border-gray-600 text-sm leading-relaxed z-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-bold text-green-300">🟢 אלפא שנתית</div>
                        <button 
                          onClick={closeAllTooltips}
                          className="text-white hover:text-red-300 transition-colors text-lg font-bold leading-none"
                          aria-label="סגור"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3">
                        <p className="text-gray-100">
                          <strong>מה זה:</strong> תשואה עודפת (או חסרה) של הקופה ביחס למדד ייחוס, אחרי התאמה לרמת הסיכון.
                        </p>
                        <p className="text-yellow-200">
                          אלפא חיובית = הקופה "ניצחה את השוק".
                        </p>
                        
                        <div className="bg-gray-800 rounded-lg p-3 space-y-1">
                          <div className="font-bold mb-2">טווחים:</div>
                          <p className="text-green-300">🟢 טוב מאוד: מעל 1.0%</p>
                          <p className="text-yellow-300">🟡 בינוני: 0%–1.0%</p>
                          <p className="text-red-300">🔴 שלילי: מתחת ל-0%</p>
                        </div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </TooltipPortal>
                )}
              </>
            )}
          </div>

          {/* יחס שארפ */}
          <div className="flex flex-col items-center relative">
            <div className="relative">
              <CircularProgress
                percentage={getSharpePercentage(sharpeRatio)}
                label="יחס שארפ"
                color={getSharpeColor(sharpeRatio)}
                size={70}
                strokeWidth={4}
                textSize="text-xs"
                labelSize="text-xs"
                showPercentage={false}
                value={sharpeRatio.toFixed(2)}
                unit=""
                maxValue={100}
              />
              <div
                ref={sharpeTooltipRef}
                className="absolute -top-1 -right-1 cursor-help"
                onMouseEnter={() => !isMobile && setShowSharpeTooltip(true)}
                onMouseLeave={() => !isMobile && setShowSharpeTooltip(false)}
                onClick={() => setShowSharpeTooltip(!showSharpeTooltip)}
              >
                <HelpCircle className="h-3 w-3 text-blue-500 hover:text-blue-600 transition-colors" />
              </div>
            </div>
            <div className="mt-2 text-center">
              <div className="text-xs text-gray-600">
                {sharpeRatio > 1.5 ? "מעולה" : 
                 sharpeRatio > 1 ? "טוב" : 
                 sharpeRatio > 0.5 ? "בינוני" : "חלש"}
              </div>
            </div>
          {showSharpeTooltip && (
              <>
                {/* טולטיפ למובייל */}
                {isMobile ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
             <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="font-bold text-blue-600">🔵 יחס שארפ</div>
                        <button 
                          onClick={closeAllTooltips}
                          className="text-gray-500 hover:text-red-500 transition-colors text-xl font-bold leading-none"
                          aria-label="סגור"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3 text-gray-700">
                        <p className="text-sm">
                          <strong>מה זה:</strong> מדד שבודק כמה תשואה הקופה נתנה עבור כל יחידת סיכון שנלקחה.
                        </p>
                        <p className="text-sm text-orange-600 font-medium">
                          ככל שהשארפ גבוה – הקופה ניהלה את הסיכון בצורה יעילה יותר.
                        </p>
                        
                        <div className="bg-blue-100 rounded-lg p-3 space-y-1 text-sm">
                          <div className="font-bold mb-2">טווחים:</div>
                          <p className="text-green-700 font-medium">🟢 טוב מאוד: 1.3 ומעלה</p>
                          <p className="text-yellow-700 font-medium">🟡 בינוני: 0.8–1.2</p>
                          <p className="text-red-700 font-medium">🔴 נמוך: מתחת ל-0.7</p>
                        </div>
                      </div>
                       <div className="flex justify-center mt-6">
           <button
              onClick={closeAllTooltips}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              הבנתי, תודה!
            </button>
          </div>
                    </div>
                  </div>
                ) : (
                  /* טולטיפ לדקסטופ */
                  <TooltipPortal targetRef={sharpeTooltipRef}>
                    <div className="bg-gray-900 text-white p-4 rounded-xl shadow-2xl max-w-xs border border-gray-600 text-sm leading-relaxed z-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-bold text-blue-300">🔵 יחס שארפ</div>
                        <button 
                          onClick={closeAllTooltips}
                          className="text-white hover:text-red-300 transition-colors text-lg font-bold leading-none"
                          aria-label="סגור"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3">
                        <p className="text-gray-100">
                          <strong>מה זה:</strong> מדד שבודק כמה תשואה הקופה נתנה עבור כל יחידת סיכון שנלקחה.
                        </p>
                        <p className="text-yellow-200">
                          ככל שהשארפ גבוה – הקופה ניהלה את הסיכון בצורה יעילה יותר.
                        </p>
                        
                        <div className="bg-gray-800 rounded-lg p-3 space-y-1">
                          <div className="font-bold mb-2">טווחים:</div>
                          <p className="text-green-300">🟢 טוב מאוד: 1.3 ומעלה</p>
                          <p className="text-yellow-300">🟡 בינוני: 0.8–1.2</p>
                          <p className="text-red-300">🔴 נמוך: מתחת ל-0.7</p>
                        </div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </TooltipPortal>
                )}
              </>
            )}
          </div>

          {/* יחס נזילות */}
          <div className="flex flex-col items-center relative">
            <div className="relative">
              <CircularProgress
                percentage={liquidityRatio}
                label="יחס נזילות"
                color="#06B6D4"
                size={70}
                strokeWidth={4}
                textSize="text-xs"
                labelSize="text-xs"
                showPercentage={true}
                maxValue={100}
              />
              <div
                ref={liquidityTooltipRef}
                className="absolute -top-1 -right-1 cursor-help"
                onMouseEnter={() => !isMobile && setShowLiquidityTooltip(true)}
                onMouseLeave={() => !isMobile && setShowLiquidityTooltip(false)}
                onClick={() => setShowLiquidityTooltip(!showLiquidityTooltip)}
              >
                <HelpCircle className="h-3 w-3 text-blue-500 hover:text-blue-600 transition-colors" />
              </div>
            </div>
            <div className="mt-2 text-center">
              <div className="text-xs text-gray-600">
                {liquidityRatio > 90 ? "גבוהה מאוד" : 
                 liquidityRatio > 75 ? "גבוהה" : 
                 liquidityRatio > 50 ? "בינונית" : "נמוכה"}
              </div>
            </div>
         
            {showLiquidityTooltip && (
              <>
                {/* טולטיפ למובייל */}
                {isMobile ? (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeAllTooltips}>
                   <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl p-4 border border-cyan-200 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="font-bold text-cyan-600">💧 יחס נזילות</div>
                        <button 
                          onClick={closeAllTooltips}
                          className="text-gray-500 hover:text-red-500 transition-colors text-xl font-bold leading-none"
                          aria-label="סגור"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3 text-gray-700">
                        <p className="text-sm">
                          <strong>מה זה:</strong> כמה מהנכסים בקופה ניתנים למימוש מהיר (נכסים סחירים).
                        </p>
                        <p className="text-sm text-orange-600 font-medium">
                          נזילות גבוהה = תגובה מהירה, גמישות גבוהה, פחות סיכון תזרימי.
                        </p>
                        
                        <div className="bg-cyan-100 rounded-lg p-3 space-y-1 text-sm">
                          <div className="font-bold mb-2">טווחים:</div>
                          <p className="text-green-700 font-medium">🟢 גבוהה: מעל 70%</p>
                          <p className="text-yellow-700 font-medium">🟡 בינונית: 50%–70%</p>
                          <p className="text-red-700 font-medium">🔴 נמוכה: מתחת ל-50%</p>
                        </div>
                      </div>
                      <div className="flex justify-center mt-6">
           <button
              onClick={closeAllTooltips}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              הבנתי, תודה!
            </button>
          </div>
                    </div>
                  </div>
                ) : (
                  /* טולטיפ לדקסטופ */
                  <TooltipPortal targetRef={liquidityTooltipRef}>
                    <div className="bg-gray-900 text-white p-4 rounded-xl shadow-2xl max-w-xs border border-gray-600 text-sm leading-relaxed z-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-bold text-cyan-300">💧 יחס נזילות</div>
                        <button 
                          onClick={closeAllTooltips}
                          className="text-white hover:text-red-300 transition-colors text-lg font-bold leading-none"
                          aria-label="סגור"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3">
                        <p className="text-gray-100">
                          <strong>מה זה:</strong> כמה מהנכסים בקופה ניתנים למימוש מהיר (נכסים סחירים).
                        </p>
                        <p className="text-yellow-200">
                          נזילות גבוהה = תגובה מהירה, גמישות גבוהה, פחות סיכון תזרימי.
                        </p>
                        
                        <div className="bg-gray-800 rounded-lg p-3 space-y-1">
                          <div className="font-bold mb-2">טווחים:</div>
                          <p className="text-green-300">🟢 גבוהה: מעל 70%</p>
                          <p className="text-yellow-300">🟡 בינונית: 50%–70%</p>
                          <p className="text-red-300">🔴 נמוכה: מתחת ל-50%</p>
                        </div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </TooltipPortal>
                )}
              </>
            )}
          </div>

          {/* סטיית תקן */}
          <div className="flex flex-col items-center relative">
            <div className="relative">
              <CircularProgress
                percentage={getStandardDeviationPercentage(standardDeviation)}
                label="סטיית תקן"
                color={getStdDevColor(standardDeviation)}
                size={70}
                strokeWidth={4}
                textSize="text-xs"
                labelSize="text-xs"
                showPercentage={false}
                value={standardDeviation.toFixed(2)}
                unit="%"
                maxValue={100}
              />
              <div
                ref={stdDevTooltipRef}
                className="absolute -top-1 -right-1 cursor-help"
                onMouseEnter={() => !isMobile && setShowStdDevTooltip(true)}
                onMouseLeave={() => !isMobile && setShowStdDevTooltip(false)}
                onClick={() => setShowStdDevTooltip(!showStdDevTooltip)}
              >
                <HelpCircle className="h-3 w-3 text-blue-500 hover:text-blue-600 transition-colors" />
              </div>
            </div>
            <div className="mt-2 text-center">
              <div className="text-xs text-gray-600">
                {standardDeviation < 5 ? "סיכון נמוך" : 
                 standardDeviation < 10 ? "סיכון בינוני" : 
                 standardDeviation < 15 ? "סיכון גבוה" : "סיכון גבוה מאוד"}
              </div>
            </div>

            {showStdDevTooltip && (
              <>
                {/* טולטיפ למובייל */}
                {isMobile ? (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 border border-orange-200 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="font-bold text-orange-600">📉 סטיית תקן</div>
                        <button 
                          onClick={closeAllTooltips}
                          className="text-gray-500 hover:text-red-500 transition-colors text-xl font-bold leading-none"
                          aria-label="סגור"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3 text-gray-700">
                        <p className="text-sm">
                          <strong>מה זה:</strong> רמת התנודתיות של הקופה בפועל – מדד לסיכון האמיתי.
                        </p>
                        <p className="text-sm text-orange-600 font-medium">
                          סטייה גבוהה = יותר סיכון, פוטנציאל גם לתשואות גבוהות יותר.
                        </p>
                        
                        <div className="bg-orange-100 rounded-lg p-3 space-y-1 text-sm">
                          <div className="font-bold mb-2">טווחים גנריים (יש להתחשב גם בסוג המסלול):</div>
                          <p className="text-green-700 font-medium">🟢 נמוכה: עד 3%</p>
                          <p className="text-yellow-700 font-medium">🟡 בינונית: 3%–7%</p>
                          <p className="text-red-700 font-medium">🔴 גבוהה: מעל 7%</p>
                        </div>
                      </div>
 <div className="flex justify-center mt-6">
           <button
           onClick={closeAllTooltips}
             className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
           >
             הבנתי, תודה!
         </button>
         </div>
                    </div>
                  </div>
                ) : (
                  /* טולטיפ לדקסטופ */
                  <TooltipPortal targetRef={stdDevTooltipRef}>
                    <div className="bg-gray-900 text-white p-4 rounded-xl shadow-2xl max-w-xs border border-gray-600 text-sm leading-relaxed z-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-bold text-orange-300">📉 סטיית תקן (12 חודשים)</div>
                        <button 
                          onClick={closeAllTooltips}
                          className="text-white hover:text-red-300 transition-colors text-lg font-bold leading-none"
                          aria-label="סגור"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3">
                        <p className="text-gray-100">
                          <strong>מה זה:</strong> רמת התנודתיות של הקופה בפועל – מדד לסיכון האמיתי.
                        </p>
                        <p className="text-yellow-200">
                          סטייה גבוהה = יותר סיכון, פוטנציאל גם לתשואות גבוהות יותר.
                        </p>
                        
                        <div className="bg-gray-800 rounded-lg p-3 space-y-1">
                          <div className="font-bold mb-2">טווחים גנריים (יש להתחשב גם בסוג המסלול):</div>
                          <p className="text-green-300">🟢 נמוכה: עד 3%</p>
                          <p className="text-yellow-300">🟡 בינונית: 3%–7%</p>
                          <p className="text-red-300">🔴 גבוהה: מעל 7%</p>
                        </div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2">
                        <div className="w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </TooltipPortal>
                )}
              </>
            )}
          </div>
        </div>
      </div>     
       <div className="mt-6 md:mt-8 bg-gray-50 rounded-lg md:rounded-xl p-3 md:p-4">
        <h4 className="font-semibold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">נתונים מפורטים:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 text-xs md:text-sm">
          <div className="flex items-center gap-1 py-1">
            <span className="text-gray-600">גודל הקופה:</span>
            <span className="font-semibold">
              {product.totalAssets ? 
                `${new Intl.NumberFormat('he-IL').format(product.totalAssets / 1000000)} (במיליוני ש"ח)` 
                : '-'}
            </span>
          </div>
          
          <div className="flex items-center gap-1 py-1">
            <div className="bg-yellow-200 px-2 rounded flex items-center gap-1">
              <span className="text-gray-600 font-bold">חשיפה למניות:</span>
              <span className="font-semibold text-gray-900">{stocksExposure.toFixed(2)}%</span>
            </div>
          </div>

          <div className="flex items-center gap-1 py-1">
            <div className="bg-yellow-200 px-2 rounded flex items-center gap-1">
              <span className="text-gray-600 font-bold">חשיפה לחו״ל:</span>
              <span className="font-semibold text-gray-900">{foreignExposure.toFixed(2)}%</span>
            </div>
          </div>

          <div className="flex items-center gap-1 py-1">
            <div className="bg-yellow-200 px-2 rounded flex items-center gap-1">
              <span className="text-gray-600 font-bold">חשיפה לארץ:</span>
              <span className="font-semibold text-gray-900">{domesticExposure.toFixed(2)}%</span>
            </div>
          </div>
          
          <div className="flex items-center gap-1 py-1">
            <div className="bg-yellow-200 px-2 rounded flex items-center gap-1">
              <span className="text-gray-600 font-bold">חשיפה למט״ח:</span>
              <span className="font-semibold text-gray-900">{currencyExposure.toFixed(2)}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const CircularProgress = ({ 
  percentage, 
  label, 
  color, 
  size, 
  strokeWidth, 
  textSize, 
  labelSize, 
  showPercentage = true, 
  value, 
  unit, 
  maxValue,
  animationDuration = 10000, // משך האנימציה במילישניות
  animateOnMount = true, // האם להפעיל אנימציה בטעינה
  repeatCount = 1, // 5 פעמים כמו שביקשת
  repeatDelay = 500 // 10 שניות הפרש כמו שביקשת
}) => {
  const [animatedPercentage, setAnimatedPercentage] = useState(animateOnMount ? 0 : percentage);
  const [animatedValue, setAnimatedValue] = useState(animateOnMount ? 0 : value);
  const [currentRepeat, setCurrentRepeat] = useState(0);

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (animatedPercentage / 100) * circumference;

  useEffect(() => {
    if (!animateOnMount) return;

    let timeoutId;
    let isActive = true; // כדי לוודא שהקומפוננטה עדיין רכובה
    
    const runAnimation = (repeatIndex) => {
      if (!isActive) return; // אם הקומפוננטה לא רכובה, תפסיק
      
      const startTime = Date.now();
      const startPercentage = 0; // תמיד נתחיל מ-0 בכל חזרה
      const startValue = 0;
      const targetPercentage = percentage;
      const targetValue = value || 0;

      setCurrentRepeat(repeatIndex + 1);

      const animate = () => {
        if (!isActive) return; // בדיקה נוספת
        
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / animationDuration, 1);

        // easeOutCubic function for smooth animation
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
        const easedProgress = easeOutCubic(progress);

        const currentPercentage = startPercentage + (targetPercentage - startPercentage) * easedProgress;
        const currentValue = startValue + (targetValue - startValue) * easedProgress;

        setAnimatedPercentage(currentPercentage);
        if (value !== undefined) {
          setAnimatedValue(currentValue);
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // האנימציה הסתיימה
          if (repeatIndex < repeatCount - 1) {
            // יש עוד חזרות - איפוס לאחר השהיה של 10 שניות
            timeoutId = setTimeout(() => {
              if (!isActive) return;
              
              setAnimatedPercentage(0);
              setAnimatedValue(0);
              // התחלת החזרה הבאה לאחר השהיה קצרה נוספת
              setTimeout(() => {
                if (isActive) {
                  runAnimation(repeatIndex + 1);
                }
              }, 100);
            }, repeatDelay);
          }
        }
      };

      animate();
    };

    // התחלת האנימציה
    runAnimation(0);

    // ניקוי בעת unmount
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [percentage, value, animationDuration, animateOnMount, repeatCount, repeatDelay]);

  // עדכון מיידי כאשר animateOnMount = false
  useEffect(() => {
    if (!animateOnMount) {
      setAnimatedPercentage(percentage);
      setAnimatedValue(value || 0);
    }
  }, [percentage, value, animateOnMount]);

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {/* רקע העיגול */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="transparent"
            className="text-gray-200"
          />
          {/* העיגול המתמלא */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-75 ease-out"
            style={{
              // אנימציה חלקה יותר עם CSS
              transition: animateOnMount ? 'none' : 'stroke-dashoffset 0.3s ease-out'
            }}
          />
        </svg>
        <div className={`absolute inset-0 flex items-center justify-center ${textSize} font-bold`} dir="ltr">
        {showPercentage 
  ? `${animatedPercentage.toFixed(2)}%` 
  : `${animatedValue.toFixed(2)}${unit || ''}` // ← .toFixed(2) במקום Math.round
}
        </div>
      </div>
      <span className={`mt-2 text-center text-gray-700 font-medium ${labelSize || textSize}`}>
        {label}
      </span>
      
  
    </div>
  );
};

const InactiveFundAlert = ({ product, currentCategory, category }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  // אם הקופה פעילה - לא מציגים כלום
  if (product.isActive === true) {
    return null;
  }

  return (
    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 shadow-lg">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0 animate-pulse" />
        <div className="flex-1">
          {/* כותרת אזהרה */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-bold text-red-800">⚠️ קופה לא פעילה</h3>
          
          </div>

          {/* הסבר בסיסי */}
          <p className="text-red-700 text-sm mb-3 leading-relaxed">
            הקופה הזאת מסומנת כ<strong>לא פעילה</strong> במערכת. 
            {/* {product.activityReason && (
              <span> הסיבה: <strong>{product.activityReason}</strong></span>
            )} */}
          </p>

     
          {/* <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-red-600 hover:text-red-800 text-sm font-medium underline mb-3 transition-colors"
          >
            {showDetails ? 'הסתר פרטים' : 'מה זה אומר? לחץ לפרטים נוספים'}
          </button>

          {showDetails && (
            <div className="bg-red-100 rounded-lg p-4 space-y-3 text-sm">
              <div>
                <h4 className="font-bold text-red-800 mb-2">🔍 מה זה אומר "קופה לא פעילה"?</h4>
                <ul className="space-y-1 text-red-700">
                  <li>• אין נכסים בקופה (יתרה 0 ₪) או</li>
                  <li>• לא מתבצעות הפקדות/משיכות חדשות</li>
                  <li>• הקופה "רדומה" במערכת</li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-red-800 mb-2">⚡ מה כדאי לעשות?</h4>
                <ul className="space-y-1 text-red-700">
                  <li>• בדוק אם יש לך קופות פעילות אחרות</li>
                  <li>• שקול להעביר כספים מקופות פעילות</li>
                  <li>• התייעץ עם יועץ פנסיוני לגבי איחוד קופות</li>
                  <li>• בחן קופות חלופיות פעילות באותה קטגוריה</li>
                </ul>
              </div>

            
              {product.activityDetails && (
                <div>
                  <h4 className="font-bold text-red-800 mb-2">📊 פרטי פעילות:</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>נכסים: {product.activityDetails.hasAssets ? '✅ יש' : '❌ אין'}</div>
                    <div>הפקדות: {product.activityDetails.hasDeposits ? '✅ יש' : '❌ אין'}</div>
                    <div>משיכות: {product.activityDetails.hasWithdrawals ? '✅ יש' : '❌ אין'}</div>
                    <div>העברות: {product.activityDetails.hasInternalTransfers ? '✅ יש' : '❌ אין'}</div>
                  </div>
                </div>
              )}
            </div>
          )} */}

          {/* כפתורי פעולה */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Link
              to={`/compare/${category}`}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              🔍 מצא {currentCategory?.title} פעילה
            </Link>
            
           
    
          </div>
        </div>
      </div>
    </div>
  );
};


const ProductPage = () => {
  const { category, productId } = useParams();
  const navigate = useNavigate();
  const [isHovered1, setIsHovered1] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);
  const [comparisonFunds, setComparisonFunds] = useState([]);
const [copyStatus, setCopyStatus] = useState(false);
  const [copiedFundName, setCopiedFundName] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const tooltipRef = useRef(null);
  const [bestMatchingSpec, setBestMatchingSpec] = useState(null);
// בתוך הקומפוננט ProductPage, הוסף:
const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
const [currentLeadContext, setCurrentLeadContext] = useState(null);
const handleLeadSubmit = async (leadData) => {
  try {
    console.log('📧 Lead submitted from ProductPage:', leadData);
    
    const contactData = {
      name: leadData.name,
      email: '',
      phone: leadData.phone,
      subject: leadData.subject,
      message: leadData.message,
      category: leadData.category,
      source: leadData.source,
      sourceContext: leadData.sourceContext
    };
    
    const contactId = await saveContact(contactData);
    console.log('✅ Lead saved successfully with ID:', contactId);
    
    return true;
  } catch (error) {
    console.error('❌ Error submitting lead:', error);
    throw error;
  }
};
// פונקציה לפתיחת המודל עם קונטקסט
const openLeadModal = () => {
  const sourceContext = {
    source: 'product-page',
    category: category,
    userLocation: 'Israel',
    
    // פרטי המוצר הנוכחי
    productId: product?.id || null,
    productName: product ? getDisplayName(product) : null,
    
    // פרטי הקרן המובילה (מה-bestMatchingSpec)
    firstPlace: bestMatchingSpec?.fundIds?.length > 1 ? {
      id: bestMatchingSpec.fundIds[1], // הקרן השנייה ברשימה (הראשונה זה המוצר הנוכחי)
      name: 'הקרן המובילה בקטגוריה',
      rank: 1
    } : null
  };

  setCurrentLeadContext(sourceContext);
  setIsLeadModalOpen(true);
};
const scrollToElement = (elementId, offset = -80) => {
  const element = document.querySelector(elementId);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset + offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};
 useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [category, productId]);
  // מפה של קטגוריות
const categoryMap = {
  'study-funds': {
    title: 'קרן השתלמות',
    source: 'גמל-נט',
    color: 'blue',
    description: 'קרן השתלמות מאפשרת חיסכון עם הטבות מס וזכאות למשיכה לאחר 6 שנים'
  },
  'pension-savings': {
    title: 'קופת גמל',
    source: 'גמל-נט',
    color: 'green',
    description: 'קופת גמל לתגמולים ופיצויים עם זכאות למשיכה בגיל פרישה'
  },
  'investment-funds': {
    title: 'קופת גמל להשקעה',
    source: 'גמל-נט',
    color: 'purple',
    description: 'קופת גמל להשקעה ארוכת טווח עם גמישות במשיכה'
  },
  'insurance-savings': {
    title: 'פוליסת חיסכון',
    source: 'ביטוח-נט',
    color: 'orange',
    description: 'פוליסת חיסכון המשלבת ביטוח וחיסכון לטווח ארוך'
  },
  'insurance-management': {  // הוסף את זה
    title: 'קרן ביטוח מנהלים',
    source: 'ביטוח-נט',
    color: 'teal',
    description: 'קרנות השקעה מקצועיות בניהול חברות ביטוח'
  },
  'pension-plans': {
    title: 'קרן פנסיה',
    source: 'פנסיה-נט',
    color: 'red',
    description: 'קרן פנסיה המספקת קצבה חודשית בגיל פרישה'
  },
  'child-savings': {
    title: 'תוכנית חיסכון לכל ילד',
    source: 'גמל-נט',
    color: 'pink',
    description: 'תוכנית חיסכון ממשלתית לעתיד הילדים'
  }
};
// פונקציה לקבלת רמת סיכון לפי חשיפה למניות
 const getRiskLevelDisplay = (level) => {
    switch(level) {
      case 1:
        return {
          level: 1,
          stars: '⭐',
          color: 'text-green-600',
          bgColor: 'bg-green-50 border-green-200',
          name: 'סולידי קלאסי',
          description: 'חשיפה מניתית: 0% עד 10%'
        };
      case 2:
        return {
          level: 2,
          stars: '⭐⭐',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50 border-blue-200',
          name: 'סולידי-מאוזן',
          description: 'חשיפה מניתית: עד 25%'
        };
      case 3:
        return {
          level: 3,
          stars: '⭐⭐⭐',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50 border-yellow-200',
          name: 'כללי מאוזן',
          description: 'חשיפה מניתית: עד 53%'
        };
      case 4:
        return {
          level: 4,
          stars: '⭐⭐⭐⭐',
          color: 'text-orange-600',
          bgColor: 'bg-orange-50 border-orange-200',
          name: 'מניתי מאוזן',
          description: 'חשיפה מניתית: עד 65%'
        };
      case 5:
        return {
          level: 5,
          stars: '⭐⭐⭐⭐⭐',
          color: 'text-red-600',
          bgColor: 'bg-red-50 border-red-200',
          name: 'מניתי מלא',
          description: 'חשיפה מניתית: 65% עד 100%'
        };
      default:
        return {
          level: 0,
          stars: '❓',
          color: 'text-gray-600',
          bgColor: 'bg-gray-50 border-gray-200',
          name: 'לא ידוע',
          description: 'אין נתוני חשיפה זמינים'
        };
    }
  };


  // 🆕 הוסף את הפונקציה הזאת מחוץ ל-render
  const getRiskDetails = (level) => {
    switch(level) {
      case 1:
        return {
          whatInTrack: "לרוב השקעות שקליות – פיקדונות, אג\"ח מדינה וחברות, מק\"מים והלוואות סולידיות. לעיתים קיים רכיב מנייתי קטן – אך מינורי ואינו משמעותי.",
          forWhom: "למי שמעדיף לישון טוב בלילה ולא לרדוף אחרי השוק. למשקיעים שזקוקים לכסף כבר בשנה או בשנתיים הקרובות – ומבקשים שקט נפשי ולא תנודתיות. למשקיע הזה, הזהירות היא אסטרטגיה – לא חולשה 😉."
        };
      case 2:
        return {
          whatInTrack: "התיק מורכב ברובו מאג\"ח ממשלתיות, אג\"ח חברות, מק\"מים ופקדונות – עם רכיב מנייתי מדוד שלא יעבור את 25% מסך התיק. מסלול זה מקביל ברמת הסיכון שלו על פי הגדרת משרד האוצר לבני 60 ומעלה, שטווח ההשקעה שלהם לרוב קצר ונמוך מסף של שלוש שנים.",
          forWhom: "למשקיע שמבין שההזדמנות עדיין חשובה – אבל השמירה על מה שכבר נצבר חשובה יותר. למי שרוצה לייצר תשואה מתונה מבלי לחשוף את הפנסיה לרעשים מיותרים. למשקיע הזה, בשלב הזה של החיים – סיכון קטן הוא לא ויתור על רווח, אלא שמירה על התוצאה."
        };
      case 3:
        return {
          whatInTrack: "רוב החוסכים בישראל נמצאים במסלול זה – לרוב כברירת מחדל של הקרן או הקופה. שילוב כמעט שווה בין מניות לאג\"ח – תיק מאוזן בין סיכון ליציבות. מסלול זה מקביל ברמת הסיכון שלו על פי הגדרת משרד האוצר לחוסכים בני 50 עד 60, שטווח ההשקעה שלהם לרוב לא פחות משלוש שנים.",
          forWhom: "למשקיע שרוצה לגדול – אבל עם עוגן. למי שמתכנן לפרוש בעוד כמה שנים, ומבין שהתשואה בשנים האחרונות חשובה לא פחות מהשקט. למשקיע הזה, איזון זה לא אמצע הדרך – זו הדרך הנכונה."
        };
      case 4:
        return {
          whatInTrack: "תיק שמורכב ברובו ממניות, עם רכיב אג\"ח של כשליש – ליציבות וספיגת תנודתיות. מסלול זה מקביל ברמת הסיכון שלו על פי הגדרת משרד האוצר לחוסכים בני 50 ומטה, שהשנים עד לפרישה עדיין רבות – והסבלנות משחקת תפקיד מרכזי.",
          forWhom: "למשקיע שמבין שצמיחה אמיתית דורשת מוכנות להחזיק מעמד גם כשהשוק סוער. למי שטווח ההשקעה שלו ארוך מספיק כדי לא להיבהל ממינוס זמני בדרך לפלוס משמעותי. למשקיע הזה, תנודתיות היא לא איום – אלא תנאי הכרחי לתשואה."
        };
      case 5:
        return {
          whatInTrack: "תיק אגרסיבי שמורכב כמעט כולו ממניות – בארץ בלבד, בחו\"ל בלבד, או בשילוב בין השווקים. ללא אג\"ח – רק מניות, תנודתיות, וקצת מזומן להזדמנויות שוק. מסלול זה מתאים לרמת הסיכון שמוגדרת לבני 45 ומטה או לחוסכים בעלי טווח השקעה של שבע שנים ומעלה.",
          forWhom: "למשקיע שמבין שהזמן הוא לא רק כסף – הוא היתרון הכי גדול שיש לו. למי שלא צריך את הכסף בשנים הקרובות – ומוכן לעלות על רכבת הרים כדי להגיע רחוק. כי כמו שאמר איינשטיין: ריבית דריבית היא הפלא השמיני של העולם – מי שמבין את זה, מרוויח. ומי שלא? משלם למי שכן מבין."
        };
      default:
        return { whatInTrack: "", forWhom: "" };
    }
  };
  const currentCategory = categoryMap[category];
  // פונקציה לקבלת נתיב הלוגו
  const getCompanyLogoPath = (managingCorpLegalId) => {
    if (!managingCorpLegalId) return null;
    return `/logos/${managingCorpLegalId}.png`;
  };
  const [logoExists, setLogoExists] = useState({});
  const checkLogoExists = (managingCorpLegalId) => {
    if (!managingCorpLegalId || logoExists[managingCorpLegalId] !== undefined) return;
    
    const img = new Image();
    img.onload = () => {
      setLogoExists(prev => ({ ...prev, [managingCorpLegalId]: true }));
    };
    img.onerror = () => {
      setLogoExists(prev => ({ ...prev, [managingCorpLegalId]: false }));
    };
    img.src = getCompanyLogoPath(managingCorpLegalId);
  };
  useEffect(() => {
    if (product?.managingCorpLegalId) {
      checkLogoExists(product.managingCorpLegalId);
    }
  }, [product]);

const getLastUpdateDate = (product) => {
  // אם יש תקופת דיווח במוצר
  if (product && product.reportPeriod) {
    return formatReportPeriod(product.reportPeriod);
  }
  
  // אם יש תאריך עדכון ספציפי
  if (product && product.lastUpdated) {
    return product.lastUpdated;
  }
  
  // אם יש מידע על תאריך במבנה fundInfo
  if (product && product.fundInfo && product.fundInfo.reportPeriod) {
    return formatReportPeriod(product.fundInfo.reportPeriod);
  }
  
  // fallback למקרה שאין מידע
  return 'לא זמין';
};


  useEffect(() => {
   
    loadProductData();
  }, [category, productId]);



  const loadProductData = async () => {
    try {
      setLoading(true);
      setError(null);
      
  
      // טעינת נתוני המוצר
      const productData = await api.getProductById(productId, category);
      
      console.log('📦 Raw product data received:', productData);
      
      if (!productData) {
        console.error('❌ No product data received');
        setError('מוצר לא נמצא');
        return;
      }

     
      setProduct(productData);
const spec = await getBestMatchingSpecialization(category, productData);
setBestMatchingSpec(spec);
      // טעינת מוצרים דומים
      try {
      
        const similar = await api.getSimilarProducts(productId, category, { limit: 6 });
        // console.log('📦 Similar products received:', similar);
        setSimilarProducts(similar);
      } catch (similarError) {
        console.warn('⚠️ Failed to load similar products:', similarError);
        setSimilarProducts([]);
      }

      // הגדרת קרנות להשוואה לפי קטגוריה ומאפיינים - עכשיו מהקונפיגורציה
      try {
       
        const topFundsForCategory = getTopFundsForComparison(category, productData);
        // console.log('📦 Comparison funds:', topFundsForCategory);
        setComparisonFunds(topFundsForCategory);
      } catch (comparisonError) {
        console.warn('⚠️ Failed to get comparison funds:', comparisonError);
        setComparisonFunds([]);
      }

      // עדכון title הדף
      document.title = `${productData.name} | ${currentCategory?.title || 'מוצר חיסכון'}`;
      

    } catch (err) {
      console.error('❌ Error loading product:', err);
      console.error('❌ Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response
      });
      setError(`שגיאה בטעינת המוצר: ${err.message}`);
    } finally {
      setLoading(false);
     
    }
  };
useEffect(() => {
    if (product && currentCategory) {
      // עדכון title הדף עם אינדיקטור פעילות
      const activityStatus = product.isActive === false ? ' (לא פעילה)' : '';
      document.title = `${product.name}${activityStatus} | ${currentCategory?.title || 'מוצר חיסכון'}`;
    }
  }, [product, currentCategory]);

  // פונקציה מעודכנת לקביעת קרנות מובילות להשוואה - משתמשת רק בקונפיגורציה
  const getTopFundsForComparison = (category, currentProduct) => {
   
    // קבלת ההתמחות הראשונה מהקונפיגורציה
    const specializations = getSpecializationConfigs(category);
  
    if (specializations && specializations.length > 0) {
      const firstSpec = specializations[0];
      const fundIds = [...firstSpec.fundIds];
      
      // הוסף את הקרן הנוכחית אם היא לא ברשימה
      const currentProductId = parseInt(currentProduct.id);
      if (!fundIds.includes(currentProductId)) {
        fundIds.unshift(currentProductId);
      
      }
      
      const result = fundIds.slice(0, 10);

      return result;
    }

    // fallback אחרון - רק הקרן הנוכחית
 
    return [parseInt(currentProduct.id)];
  };

  // פונקציה לקבלת המומחיות המתאימה ביותר לקרן הנוכחית
  // בקובץ ProductPage.js - החלף את הפונקציה getBestMatchingSpecialization הקיימת:

  // פונקציה לזיהוי חכם של התמחות לפי שם הקרן
  const detectSpecializationFromName = (productName, specializations) => {
    const name = productName.toLowerCase();
    
    // מילות מפתח לזיהוי התמחויות
    const keywords = {
      'כללי': ['כללי', 'מאוזן', 'מעורב', 'בסיס'],
      'מניות': ['מניות', 'equity', 'stocks'],
      'sp500': ['s&p 500', 'sp500', 's&p500', 'אס אנד פי'],
      'sp500-גמל': ['s&p 500', 'sp500', 's&p500', 'אס אנד פי'],
      'sp500-השקעה': ['s&p 500', 'sp500', 's&p500', 'אס אנד פי'],
      'אג"ח-ממשלות': ['אגח ממשל', 'ממשלתי', 'government', 'treasury'],
      'אגח-ממשלות-גמל': ['אגח ממשל', 'ממשלתי', 'government', 'treasury'],
      'אשראי-ואגח': ['אשראי', 'אגח', 'bonds', 'credit'],
      'הלכתי': ['הלכתי', 'שרעי', 'halal', 'kosher', 'religious'],
      'עד-50': ['עד 50', 'לבני 50 ומטה', 'עד גיל 50', 'צעיר'],
      '50-60': ['50-60', '50 עד 60', 'בוגר'],
      'מעל-60': ['מעל 60', 'לבני 60 ומעלה', 'וותיק', 'senior'],
      // הוסף עוד מילות מפתח לפי הצורך...
    };
    
    // חיפוש התאמה בשם
    for (const spec of specializations) {
      const specKeywords = keywords[spec.key] || [];
      
      const hasMatch = specKeywords.some(keyword => 
        name.includes(keyword.toLowerCase())
      );
      
      if (hasMatch) {
        // console.log(`🎯 Detected specialization "${spec.key}" for product "${productName}"`);
        return spec;
      }
    }
    
    // console.log(`🤷 No specialization detected for "${productName}", using first available`);
    return specializations[0] || null;
  };
// פונקציה להמרת תקופת דיווח לתאריך קריא
const formatReportPeriod = (reportPeriod) => {
  if (!reportPeriod) return 'לא זמין';
  
  const periodStr = reportPeriod.toString();
  if (periodStr.length !== 6) return reportPeriod;
  
  const year = periodStr.slice(0, 4); // 2025
  const monthNum = parseInt(periodStr.slice(-2)); // 04 -> 4
  
  const months = [
    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
  ];
  
  if (monthNum >= 1 && monthNum <= 12) {
    return `${months[monthNum - 1]} ${year}`;
  }
  
  return reportPeriod;
};
  // פונקציה לקבלת המומחיות המתאימה ביותר לקרן הנוכחית - מעודכנת עם זיהוי חכם
const getBestMatchingSpecialization = async (category, currentProduct) => {
  return await getEnhancedSpecializationMatch(currentProduct, category, api);
};

  const handleShare = async () => {
    const url = window.location.href;
    const title = `${product.name} - ${currentCategory?.title}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (err) {
        console.log('שיתוף בוטל');
      }
    } else {
      navigator.clipboard.writeText(url);
      alert('הקישור הועתק ללוח');
    }
  };

  const handleCopyFundName = async () => {
    if (!product) return;
    
    const textToCopy = `${getDisplayName(product)} - מספר באוצר ${product.id}`;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedFundName(true);
      toast.success('הועתק ללוח!');
      setTimeout(() => setCopiedFundName(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error('העתקה נכשלה');
    }
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
  };

  const getReturnColor = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'text-gray-400';
    const val = parseFloat(value);
    if (val >= 8) return 'text-emerald-600';
    if (val >= 5) return 'text-green-600';
    if (val >= 2) return 'text-blue-600';
    if (val >= 0) return 'text-yellow-600';
    return 'text-red-500';
  };

  const formatValue = (value, suffix = '%', decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return `${value.toFixed(decimals)}${suffix}`;
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('he-IL').format(value / 1000000) + ' מיליון ש"ח';
  };

  // 🆕 פונקציה מעודכנת לקבלת שם החודש מתקופת הדיווח
  const getCurrentMonth = () => {
    const months = [
      'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
      'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
    ];
    
    // נסה לקבל מתקופת הדיווח אם קיימת
    if (product && product.reportPeriod) {
      const periodStr = product.reportPeriod.toString();
      if (periodStr.length === 6) {
        const monthNum = parseInt(periodStr.slice(-2));
        if (monthNum >= 1 && monthNum <= 12) {
         
          return months[monthNum - 1];
        }
      }
    }
    

    return months[new Date().getMonth()];
  };
 const TooltipContent = ({ riskData, details, onClose }) => (
  <div className={`bg-white rounded-lg border-2 shadow-xl p-4 max-w-sm ${riskData.bgColor} relative`}>
    {/* 🆕 כפתור סגירה */}
    <button
      onClick={onClose}
      className="absolute top-2 right-2 text-gray-500 hover:text-red-500 transition-colors text-lg font-bold leading-none w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50"
      aria-label="סגור"
    >
      ×
    </button>
    
    <div className="text-center mb-3">
      <div className="text-sm font-bold">
        שם המסלול: {riskData.name}
      </div>
      <div className="text-xs text-gray-600">
        {riskData.description}
      </div>
    </div>

    <div className="space-y-2">
      <div>
        <div className="text-xs font-bold text-gray-800 mb-1">מה יש במסלול:</div>
        <div className="text-xs text-gray-700 leading-tight">
          {details.whatInTrack}
        </div>
      </div>
      <div>
        <div className="text-xs font-bold text-gray-800 mb-1">למי זה מתאים:</div>
        <div className="text-xs text-gray-700 leading-tight">
          {details.forWhom}
        </div>
      </div>
    </div>
  </div>
);
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-lg font-medium text-gray-600">טוען נתוני מוצר...</div>
          <div className="text-sm text-gray-500 mt-2">מזהה מוצר: {productId}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">שגיאה</h1>
          <p className="text-gray-600 mb-2">{error}</p>
          <div className="text-sm text-gray-500 mb-6">
            מזהה מוצר: {productId} | קטגוריה: {category}
          </div>
          <div className="space-y-3">
            <button
              onClick={() => {
               
                loadProductData();
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
            >
              נסה שוב
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium"
            >
              חזור אחורה
            </button>
            <Link
              to="/"
              className="block w-full bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium text-center"
            >
              דף הבית
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    console.log('⚠️ Product is null, but not in error state');
    return null;
  }



  return (
    <div className="min-h-screen bg-gray-50">
{/* כותרת עליונה */}
  <div className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8"> {/* ← הגדלתי את ה-padding */}

        {/* 🆕 שורת חיפוש */}
        <div className="mb-6 md:mb-8"> {/* ← הגדלתי את המרווח התחתון */}
          <OptimizedSearchAutocomplete
            selectedCategory={category}
            onSearchSubmit={() => {
              // לא צריך לעשות כלום - החיפוש רק מציג הצעות
              // הניווט קורה ב-selectSuggestion של הקומפוננט
            }}
            placeholder={`חפש ${currentCategory?.title} אחרת...`}
            loading={false}
          />
        </div>
        {/* breadcrumb מעוצב */}
        <div className="mb-6 md:mb-8">
          <nav className="flex items-center gap-2 text-sm overflow-x-auto">
            <Link 
              to="/" 
              className="text-gray-500 hover:text-blue-600 whitespace-nowrap transition-colors px-2 py-1 rounded-md hover:bg-blue-50"
            >
              דף הבית
            </Link>
            <span className="text-gray-300">›</span>
            <Link 
              to={`/compare/${category}`} 
              className="text-gray-500 hover:text-blue-600 whitespace-nowrap transition-colors px-2 py-1 rounded-md hover:bg-blue-50"
            >
              השוואת {currentCategory?.title}
            </Link>
            <span className="text-gray-300">›</span>
            <span className="text-gray-900 font-semibold truncate px-2 py-1 bg-gray-50 rounded-md">
              {getDisplayName(product)}
            </span>
          </nav>
        </div>

       {/* כותרת ופרטים */}
        <div className="relative">
          
 {/* כותרת ופרטים */}
{/* כותרת ופרטים */}
<div className="relative">
  
  {/* הכפתורים העליונים נשארים כמו שהיו */}
  <div className="absolute top-0 inset-x-0 flex items-center justify-between z-10 px-2">
    
    {/* כפתור מועדפים - צד ימין */}
    <FavoritesButton
      product={{
        id: product.id,
        name: getDisplayName(product),
        company: product.company,
        category: category
      }}
      size="small"
      showLabel={true}
      className="flex items-center gap-1 md:gap-2 p-1 md:p-2 bg-white/90 backdrop-blur-sm shadow-md hover:shadow-lg border border-gray-200 hover:border-red-300 rounded-lg transition-all duration-300 transform hover:scale-105"
      labelText="הוסף למועדפים"
    />

    {/* כפתורי שיתוף והעתקה - צד שמאל */}
    <div className="flex items-center gap-2">
      {/* כפתור העתקה */}
      <button
        onClick={handleCopyFundName}
        className="flex items-center gap-1 md:gap-2 p-1 md:p-2 bg-white/90 backdrop-blur-sm hover:bg-gray-50 text-gray-600 hover:text-gray-700 rounded-lg shadow-md hover:shadow-lg border border-gray-200 hover:border-gray-300 transition-all duration-300 transform hover:scale-105"
        title="העתק שם קרן"
      >
        {copiedFundName ? (
          <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500" />
        ) : (
          <Copy className="w-3 h-3 md:w-4 md:h-4" />
        )}
      </button>

      {/* כפתור שיתוף */}
      <button
        onClick={handleShare}
        className="flex items-center gap-1 md:gap-2 p-1 md:p-2 bg-white/90 backdrop-blur-sm hover:bg-gray-50 text-gray-600 hover:text-blue-600 rounded-lg shadow-md hover:shadow-lg border border-gray-200 hover:border-blue-300 transition-all duration-300 transform hover:scale-105"
        title="שתף מוצר"
      >
        <Share2 className="w-3 h-3 md:w-4 md:h-4" />
        <span className="text-xs md:text-sm font-medium">שיתוף</span>
      </button>
    </div>
  </div>

  {/* תוכן מרכזי */}
  <div className="text-center space-y-6 pt-8 md:pt-12">
    
<div className="pt-4 md:pt-0">
  <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-1 md:mb-2 leading-tight">
    {getDisplayName(product)}
  </h1>
</div>

    {/* כפתורי השוואה - שורה נפרדת מתחת לכותרת */}
    <div className="flex justify-between items-center px-4 py-2">
      
      {/* כפתור השוואה חכמה - צד ימין */}
      <button
        onClick={() => scrollToElement('.fund-comparison-component')}
        className="flex flex-col items-center gap-1 p-2 md:p-3 bg-white/90 backdrop-blur-sm hover:bg-blue-50 text-gray-600 hover:text-blue-600 rounded-xl shadow-lg hover:shadow-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 transform hover:scale-105"
        title="השוואה חכמה בין קרנות"
      >
        <Search className="w-4 h-4 md:w-6 md:h-6" />
        <span className="text-xs md:text-sm font-medium text-center leading-tight">
          השוואה מהירה<br className="md:hidden" />
          <span className="hidden md:inline"> </span>בין 2 קרנות
        </span>
      </button>
      
      {/* כפתור גרפים - צד שמאל */}
      <button
        onClick={() => scrollToElement('.returns-chart-component')}
        className="flex flex-col items-center gap-1 p-2 md:p-3 bg-white/90 backdrop-blur-sm hover:bg-green-50 text-gray-600 hover:text-green-600 rounded-xl shadow-lg hover:shadow-xl border border-gray-200 hover:border-green-300 transition-all duration-300 transform hover:scale-105"
        title="השוואה תקופתית אינטרקטיבית"
      >
        <TrendingUp className="w-4 h-4 md:w-6 md:h-6" />
        <span className="text-xs md:text-sm font-medium text-center leading-tight">
          השוואה מקיפה<br className="md:hidden" />
          <span className="hidden md:inline"> </span>רב תקופתית
        </span>
      </button>
      
    </div>

    {/* לוגו במרכז - ללא כפתורים בצדדים */}
    {product.managingCorpLegalId && logoExists[product.managingCorpLegalId] && (
      <div className="flex justify-center items-center">
        <img
          src={getCompanyLogoPath(product.managingCorpLegalId)}
          alt={`לוגו ${product.company}`}
          className="h-24 md:h-36 lg:h-44 w-auto max-w-xs md:max-w-md object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
            setLogoExists(prev => ({ ...prev, [product.managingCorpLegalId]: false }));
          }}
        />
      </div>
    )}

    {/* אם אין לוגו - רווח ריק */}
    {(!product.managingCorpLegalId || !logoExists[product.managingCorpLegalId]) && (
      <div className="py-4">
        {/* רווח ריק במקום הלוגו */}
      </div>
    )}

  </div>
</div>

          

        </div>

<div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 mt-6">
  {/* כותרת עם כפתור שיתוף */}
  <div className="flex items-center justify-between mb-2">
    <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
      <Info className="h-4 w-4 text-blue-600" />
      פרטי הקרן
    </h3>
    
    {/* כפתור שתף פרטי מסלול */}
<CompactExportButton 
  product={product}
  category={category}
  currentCategory={currentCategory}
  formatReportPeriod={formatReportPeriod}
  getLastUpdateDate={getLastUpdateDate}
  lastUpdated={getLastUpdateDate(product)}
/>
  </div>
                 
  {/* גריד קומפקטי */}
  <div className="grid grid-cols-2 lg:grid-cols-2 gap-x-2 gap-y-1 text-[10px] lg:text-xs">
    
    {/* סוג מוצר השקעה */}
    {product.classification && (
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-0.5 lg:py-1.5 border-b border-gray-100">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-gray-600 font-bold">סוג מוצר השקעה:</span>
          <span className="font-bold text-gray-900">{product.classification}</span>
        </div>
        
        {/* 🆕 אינדיקטור פעילות - מותאם מובייל */}
        {product.isActive === false && (
          <div className="mr-2 flex-shrink-0">
            <span className="bg-red-100 text-red-700 text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border border-red-200 font-medium whitespace-nowrap">
              <span className="hidden sm:inline">❌ לא פעילה</span>
              <span className="sm:hidden">❌</span>
            </span>
          </div>
        )}
      </div>
    )}
     
    {/* מספר קופה באוצר עם כפתור העתקה */}
    <div className="flex items-center gap-1 py-0.5 lg:py-1.5 border-b border-gray-100 bg-yellow-200 px-2 rounded">
      <span className="text-gray-600 font-bold bg-yellow-200">מספר קופה באוצר:</span>
      <span className="font-bold text-gray-900 bg-yellow-200 select-all" id={`fund-id-${product.id}`}>
        {product.id}
      </span>
      <button
        onClick={async (e) => {
          try {
            await navigator.clipboard.writeText(product.id.toString());
            setCopyStatus(true);
            
            // הסתר את ההודעה אחרי 2 שניות
            setTimeout(() => {
              setCopyStatus(false);
            }, 2000);
            
          } catch (error) {
            console.error('Copy failed:', error);
            // fallback - בחירה אוטומטית של הטקסט
            try {
              const textElement = document.getElementById(`fund-id-${product.id}`);
              if (textElement) {
                const range = document.createRange();
                range.selectNode(textElement);
                window.getSelection().removeAllRanges();
                window.getSelection().addRange(range);
              }
            } catch (selectionError) {
              console.error('Selection fallback failed:', selectionError);
              // fallback אחרון - alert עם המספר
              alert(`מספר קופה: ${product.id}`);
            }
          }
        }}
        className={`p-1 rounded transition-all duration-200 ml-1 transform hover:scale-110 ${
          copyStatus 
            ? 'bg-green-200 hover:bg-green-300' 
            : 'hover:bg-yellow-300'
        }`}
        title={copyStatus ? 'הועתק!' : 'העתק מספר קופה'}
      >
        {copyStatus ? (
          <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        ) : (
          <Copy className="w-3 h-3 text-gray-600 hover:text-blue-600" />
        )}
      </button>
      
      {/* הודעת הצלחה זעירה */}
      {copyStatus && (
        <span className="text-xs text-green-600 font-medium animate-in fade-in slide-in-from-left duration-300">
          הועתק!
        </span>
      )}
    </div>
     
    {/* חברה מנהלת */}
    {product.company && (
      <div className="flex items-center gap-1 py-0.5 lg:py-1.5 border-b border-gray-100">
        <span className="text-gray-600 font-bold">חברה מנהלת:</span>
        <span className="font-bold text-gray-900">{product.company}</span>
      </div>
    )}
     
    {/* תאריך הקמה */}
    {(product.inceptionDate || product.fundInfo?.inceptionDate) && (
      <div className="flex items-center gap-1 py-0.5 lg:py-1.5 border-b border-gray-100">
        <span className="text-gray-600 font-bold">תאריך הקמה:</span>
        <span className="font-bold text-gray-900">{product.inceptionDate || product.fundInfo?.inceptionDate}</span>
      </div>
    )}
     
    {/* מסלול השקעה */}
    {(product.specialization || product.fundInfo?.specialization) && (
      <div className="flex items-center gap-1 py-0.5 lg:py-1.5 border-b border-gray-100">
        <span className="text-gray-600 font-bold">מסלול השקעה:</span>
        <span className="font-bold text-gray-900">{product.specialization || product.fundInfo?.specialization}</span>
      </div>
    )}
     
    {/* תקופת דיווח אחרונה */}
    {product.reportPeriod && (
      <div className="flex items-center gap-1 py-0.5 lg:py-1.5">
        <span className="text-gray-600 font-bold">תקופת דיווח אחרונה:</span>
        <span className="font-bold text-gray-900">{formatReportPeriod(product.reportPeriod)}</span>
      </div>
    )}
  </div>
  
  {/* רמת סיכון - נשאר כמו שהיה */}
  {product?.isActive && (() => {
    // 🎯 שימוש ישיר ברמת הסיכון המוכנה מהמוצר
    const riskData = product.riskLevel 
      ? getRiskLevelDisplay(product.riskLevel.level) 
      : getRiskLevelDisplay(0); // fallback אם אין נתון
    
    const details = getRiskDetails(riskData.level);

    return (
      <div className="mt-3 pt-2 border-t border-gray-200 text-center">
        <div className="text-xs lg:text-sm">
          <span className="text-gray-600 font-bold">רמת סיכון: </span>
          <span 
            ref={tooltipRef}
            className={`font-bold ${riskData.color} cursor-pointer 
                       relative inline-block group
                       hover:scale-110 active:scale-95 
                       transition-all duration-300 ease-in-out transform
                       animate-pulse hover:animate-bounce`}
            style={{
              textShadow: '0 0 8px currentColor',
              animation: 'gentle-bounce 3s ease-in-out infinite'
            }}
            onMouseEnter={() => !isMobile && setShowTooltip(true)}
            onMouseLeave={() => !isMobile && setShowTooltip(false)}
            onClick={() => isMobile && setShowTooltip(!showTooltip)}
          >
            {/* רקע זוהר */}
            <span 
              className="absolute inset-0 rounded-md opacity-30 animate-ping"
              style={{ 
                backgroundColor: 'currentColor',
                animationDuration: '2s'
              }}
            />
            
            {/* הטקסט */}
            <span className="relative z-10 drop-shadow-lg">
              {riskData.level} מתוך 5
            </span>
            
            {/* אמוג'י מהבהב */}
            {isMobile && (
              <span className="text-xs mr-1 opacity-70 animate-bounce delay-300">
                💫
              </span>
            )}
            
            {/* כוכבים מסתובבים */}
            <span className="absolute -top-2 -right-2 text-xs animate-spin" style={{animationDuration: '3s'}}>
              ⭐
            </span>
          </span>
        </div>
        <div className="text-xs text-gray-700 font-medium mt-1">
          {riskData.name}
        </div>
{/* הוספה חדשה: הצגת סוג פנסיה - רק עבור קרנות פנסיה */}
{category === 'pension-plans' && (
  <div className="mt-2">
    {/* פנסיה מקיפה */}
    {product.isComprehensivePension === true && (
      <div className="bg-blue-100 border border-blue-300 rounded-md px-2 py-1">
        <span className="text-blue-800 font-bold text-xs">
          🏛️ פנסיה מקיפה 
        </span>
      </div>
    )}
    
    {/* פנסיה משלימה */}
    {product.isComplementaryPension === true && (
      <div className="bg-purple-100 border border-purple-300 rounded-md px-2 py-1">
        <span className="text-purple-800 font-bold text-xs">
          🔄 פנסיה משלימה 
        </span>
      </div>
    )}
  </div>
)}
        {/* הטוולטיפים נשארים כמו שהם... */}
        {isMobile && showTooltip && (
          <div className="mt-3 animate-in slide-in-from-top duration-300 ease-out">
            <TooltipContent 
              riskData={riskData} 
              details={details} 
              onClose={() => setShowTooltip(false)} 
            />
          </div>
        )}

        {!isMobile && showTooltip && (
          <TooltipPortal targetRef={tooltipRef}>
            <div className="animate-in fade-in slide-in-from-bottom-4 zoom-in-95 duration-300 ease-out">
              <TooltipContent 
                riskData={riskData} 
                details={details} 
                onClose={() => setShowTooltip(false)} 
              />
            </div>
          </TooltipPortal>
        )}
        {product.exposures?.currency <= 10 && (
        <div className="mt-2 bg-yellow-100 border border-yellow-300 rounded-md px-2 py-1">
          <span className="text-yellow-800 font-bold text-xs">
            🛡️ קופה מגודרת למט״ח (חשיפה {product.exposures.currency.toFixed(2)}%)
          </span>
        </div>
      )}
 {/* 2. חשיפה גבוהה למט"ח - מעל 60% */}
{product.exposures?.currency > 60 && (
  <div className="mt-2 bg-orange-100 border border-orange-300 rounded-md px-2 py-1">
    <span className="text-orange-800 font-bold text-xs">
      🌍 המערכת זיהתה חשיפה גבוהה למט״ח ברמה של למעלה מ-60% (חשיפה {product.exposures.currency.toFixed(2)}%)
    </span>
  </div>
)}

{/* 3. חשיפה גבוהה לחו"ל - מעל 90% */}
{product.exposures?.foreign > 90 && (
  <div className="mt-2 bg-blue-100 border border-blue-300 rounded-md px-2 py-1">
    <span className="text-blue-800 font-bold text-xs">
      🌐 המערכת זיהתה חשיפה גבוהה לחו״ל ברמה של למעלה מ-90% (חשיפה {product.exposures.foreign.toFixed(2)}%)
    </span>
  </div>
)}

{/* 4. חשיפה גבוהה לישראל - מעל 90% */}
{(() => {
  const domesticExposure = product.exposures?.foreign >= 100 ? 0 : Math.max(0, 100 - (product.exposures?.foreign || 0));
  return domesticExposure > 90 && (
    <div className="mt-2 bg-green-100 border border-green-300 rounded-md px-2 py-1">
      <span className="text-green-800 font-bold text-xs">
        🇮🇱 המערכת זיהתה חשיפה גבוהה לשוק הישראלי ברמה של למעלה מ-90% (חשיפה {domesticExposure.toFixed(2)}%)
      </span>
    </div>
  );
})()}
      </div>
    );
  })()}
</div>

      </div>
    </div>

      {/* תוכן ראשי */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">

  <InactiveFundAlert 
          product={product} 
          currentCategory={currentCategory} 
          category={category} 
        />
<div className="flex items-center justify-center py-6">
  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
  
  <div className="flex-1 h-px bg-gradient-to-l from-transparent via-gray-300 to-transparent"></div>
</div>
{/* הצג רק אם הקופה פעילה */}
{product.isActive !== false && (
  <>
    <ReturnsSection 
      product={product}
      category={category}
      currentProductId={product.id}
       matchingSpecialization={bestMatchingSpec}
        lastUpdated={product.reportPeriod || getLastUpdateDate(product)}
    />

    <ExposuresSection product={product} />
    <ReturnsDisplay 
  returns={product.returns} 
  lastUpdate={product.reportPeriod}
  productName={product.name}
  productId={product.id}
  categoryId={category}
/>
    <FundDataExportSection 
      product={product}
      category={category}
      currentCategory={currentCategory}
      formatReportPeriod={formatReportPeriod}
      getLastUpdateDate={getLastUpdateDate}
      lastUpdated={getLastUpdateDate(product)}
    />


        {/* השוואת תשואות - טבלה מהקומפוננט - עכשיו משתמש בקונפיגורציה */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">
            השוואת תשואות בין "{product.name}" למובילות בקבוצה:
          </h3>
          
          {bestMatchingSpec.fundIds.length > 0 && (
     <ProductComparisonTable
     category={category}
     title="השוואה עם קרנות מובילות"
     fundIds={bestMatchingSpec.fundIds}  // ← זה נכון!
     currentProductId={product.id}
      
   />
   
       
          )}
        </div>
{/* מפריד עם התמונה */}
{/* <div className="flex items-center justify-center py-6"> 
  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
  <div className="px-4">
    <img 
      src="/fightBearBull.png" 
      alt="Fight Bear Bull" 
     className="relative w-40 h-70 lg:w-80 lg:h-60 object-contain group-hover:scale-105 transition-all duration-500 cursor-pointer"
      style={{
        opacity: 0.9,
        maskImage: 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)',
        maskComposite: 'intersect'
      }}
    />
  </div>
  <div className="flex-1 h-px bg-gradient-to-l from-transparent via-gray-300 to-transparent"></div>
</div> */}
       
<div className="space-y-4 md:space-y-8">
  {/* קומפוננטת השוואה */}

<div className="bg-white rounded-xl md:rounded-2xl shadow-md md:shadow-lg border border-gray-100 p-3 md:p-6 mb-4 md:mb-8 returns-chart-component">
  <ReturnsChart 
    product={product} 
    category={category} 
    matchingSpecialization={bestMatchingSpec}
    lastUpdated={getLastUpdateDate(product)} 
  />
</div>



<button
  onClick={openLeadModal}
  className="group relative w-full overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-purple-600 hover:via-blue-600 hover:to-indigo-600 text-white font-bold py-3 md:py-4 px-4 md:px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
>
  {/* רקע זוהר */}
  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-xl blur opacity-30 group-hover:opacity-50 animate-pulse -z-10"></div>
  
  {/* גלים */}
  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12 group-hover:translate-x-full transition-transform duration-1000 opacity-15"></div>

  {/* תוכן הכפתור */}
  <div className="relative flex items-center justify-center gap-2 md:gap-3">
 <TrendingUp className="h-5 w-5 animate-bounce group-hover:animate-spin transition-all duration-300" />
    <span className="text-sm md:text-base bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent font-extrabold">
      קח אותי לקרן המובילה
    </span>
    <span className="text-lg animate-pulse group-hover:animate-bounce transition-all duration-300" style={{ animationDuration: '2.5s' }}>✨</span>
  </div>
</button>
  {/* גרף תשואות - מותאם למובייל */}
{/* GIF מעל הקומפוננטה */}
{/* GIF מעל הקומפוננטה - מוקטן בדקסטופ */}
<div className="w-full mb-4 md:mb-6 flex justify-center">
  <div className="w-full md:w-2/3 lg:w-2/3 xl:w-1/2">
    <img 
      src="/compare2Funds.jpeg" 
      alt="השוואה בין 2 קרנות"
      className="w-full h-auto rounded-xl shadow-md"
      loading="lazy"
    />
  </div>
</div>

{/* קומפוננטת השוואה */}
<div className="w-full fund-comparison-component">
  <FundComparisonComponent
    category={category}
    currentProductId={product.id}
    currentProduct={product}
    matchingSpecialization={bestMatchingSpec}
    className="mb-4 md:mb-8"
    lastUpdated={getLastUpdateDate(product)}
  />
</div>
</div>
{/* <div className="px-2 md:px-0">

<button
  onClick={openLeadModal}
  className="group relative w-full overflow-hidden bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 hover:from-pink-600 hover:via-purple-600 hover:to-blue-600 text-white font-bold py-3 md:py-4 px-4 md:px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
>
  
  <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-xl blur opacity-30 group-hover:opacity-50 animate-pulse -z-10"></div>

  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12 group-hover:translate-x-full transition-transform duration-1000 opacity-15"></div>

  <div className="relative flex items-center justify-center gap-2 md:gap-3">
    <span className="text-xl animate-pulse group-hover:animate-bounce transition-all duration-300">🎯</span>
    <span className="text-sm md:text-base bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent font-extrabold text-center leading-tight">
      אני מוכן להפסיק לחפש - קח אותי לקרן המובילה
    </span>
    <span className="text-lg animate-bounce group-hover:animate-spin transition-all duration-300" style={{ animationDuration: '2.5s' }}>💎</span>
  </div>
</button>
</div> */}
     
    </>
)}
{product.isActive === false && (
  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
    <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2">
      💡 הצעות חלופיות פעילות
    </h3>
    <p className="text-blue-700 mb-4">
      מכיוון שהקופה הנוכחית לא פעילה, אולי תרצה לבחון את האפשרויות הפעילות הבאות:
    </p>
    
    <div className="space-y-2">
      <Link
        to={`/compare/${category}`}
        className="block bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors text-center"
      >
        🔍 רשימת כל ה{currentCategory?.title} הפעילות
      </Link>
      
      <Link
        to="/"
        className="block bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium transition-colors text-center"
      >
        🏠 חזור לדף הבית לבחירת קטגוריה אחרת
      </Link>
    </div>
  </div>
)}
 {/* כפתור Call to Action דינמי לפי סוג המוצר */}{/* כפתור Call to Action דינמי לפי סוג המוצר */}
<div className="mb-8">
  <button
    onClick={openLeadModal}
    className="group relative w-full overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-600 to-green-600 hover:from-cyan-600 hover:via-teal-600 hover:to-emerald-600 text-white font-bold py-4 md:py-5 px-4 md:px-6 rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300"
  >
    {/* רקע זוהר */}
    <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 via-cyan-500 to-green-600 rounded-xl blur opacity-30 group-hover:opacity-60 animate-pulse -z-10"></div>
    
    {/* גל נע */}
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent transform -skew-x-12 group-hover:translate-x-full transition-transform duration-1000 opacity-10"></div>

    {/* תוכן הכפתור */}
    <div className="relative flex items-center justify-center gap-2 md:gap-3">
      <span className="text-xl animate-bounce group-hover:animate-spin transition-all duration-300">✅</span>
      <span className="text-sm md:text-base bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent font-extrabold text-center leading-tight">
        {(() => {
          switch(currentCategory?.title) {
            case 'קרן השתלמות':
              return 'הבנתי מה זאת קרן השתלמות – כעת קח אותי לקרן המובילה';
            case 'קופת גמל':
              return 'הבנתי מה זאת קופת גמל – כעת קח אותי לקופה המובילה';
            case 'קופת גמל להשקעה':
              return 'הבנתי מה זאת קופת גמל להשקעה – כעת קח אותי לקופה המובילה';
            case 'קרן פנסיה':
              return 'הבנתי מה זאת קרן פנסיה – כעת קח אותי לקרן המובילה';
            case 'פוליסת חיסכון':
              return 'הבנתי מה זאת פוליסת חיסכון – כעת קח אותי לפוליסה המובילה';
            case 'קרן ביטוח מנהלים':
              return 'הבנתי מה זאת קרן ביטוח מנהלים – כעת קח אותי לקרן המובילה';
            case 'תוכנית חיסכון לכל ילד':
              return 'הבנתי מה זאת תוכנית חיסכון לילד – כעת קח אותי לתוכנית המובילה';
            default:
              return 'הבנתי את המוצר – כעת קח אותי למוצר המוביל';
          }
        })()}
      </span>
      <span className="text-lg animate-pulse group-hover:animate-bounce transition-all duration-300" style={{ animationDuration: '2.5s' }}>🎯</span>
    </div>
  </button>
</div>
<RouteScannerCTA/>
        {/* הערת אחריות */}
  {/* הערת אחריות */}
<div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
  <div className="flex items-start gap-3">
    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
    <div>
      <div className="font-medium text-blue-800 mb-3">הבהרה:</div>
      <div className="text-sm text-blue-700 space-y-2 leading-relaxed">
        <p>
          המידע באתר גמל נט AI נועד להנגיש תובנות והשוואות 📊, אך אינו מהווה ייעוץ פנסיוני, פיננסי או השקעות, ואינו תחליף לייעוץ אישי מקצועי.
        </p>
        <p>
          כל החלטה צריכה להיעשות תוך התאמה אישית ובליווי בעל רישיון מוסמך 📄✅ בלבד.
        </p>
        <p>
          תשואות עבר אינן מעידות בהכרח על ביצועים עתידיים ⏳, ואין לראות בנתונים באתר התחייבות או המלצה.
        </p>
        <p>
          אנו עושים מאמץ כנה – בשילוב מערכות בינה מלאכותית מתקדמות 🧠 – לספק מידע מדויק, ברור ואובייקטיבי, אך האחריות לשימוש בו ולפרשנותו היא בידי המשתמש בלבד.
        </p>
      </div>
    </div>
  </div>
</div>
      </div>
      <LeadModal
  isOpen={isLeadModalOpen}
  onClose={() => setIsLeadModalOpen(false)}
  onSubmit={handleLeadSubmit}
  sourceContext={currentLeadContext}
/>
    </div>
  );
};

export default ProductPage;